import { AppLanguage, detectLanguage } from './language';

const en = {
  deleteVideoConfirm: 'Delete "{title}"? This cannot be undone.',
  videoDeleted: 'Video deleted.',
  videoDeleteFailed: 'Failed to delete video. Please try again.',
  sessionExpired: 'Your session has expired. Please log in again.',
  logoutSuccess: 'Logout successful! Redirecting...',
} as const;

export type VideoManagementMessageKey = keyof typeof en;
export type VideoManagementMessageVars = Record<string, string | number>;

const zh: Record<VideoManagementMessageKey, string> = {
  deleteVideoConfirm: '删除“{title}”？此操作无法撤销。',
  videoDeleted: '视频已删除。',
  videoDeleteFailed: '删除视频失败，请重试。',
  sessionExpired: '登录已过期，请重新登录。',
  logoutSuccess: '已退出登录！正在跳转...',
};

const es: Record<VideoManagementMessageKey, string> = {
  deleteVideoConfirm: '¿Eliminar "{title}"? Esta acción no se puede deshacer.',
  videoDeleted: 'Video eliminado.',
  videoDeleteFailed: 'No se pudo eliminar el video. Inténtalo de nuevo.',
  sessionExpired: 'Tu sesión ha caducado. Inicia sesión de nuevo.',
  logoutSuccess: '¡Sesión cerrada! Redirigiendo...',
};

const fr: Record<VideoManagementMessageKey, string> = {
  deleteVideoConfirm: 'Supprimer « {title} » ? Cette action est irréversible.',
  videoDeleted: 'Vidéo supprimée.',
  videoDeleteFailed: 'Impossible de supprimer la vidéo. Veuillez réessayer.',
  sessionExpired: 'Votre session a expiré. Veuillez vous reconnecter.',
  logoutSuccess: 'Déconnexion réussie ! Redirection...',
};

const de: Record<VideoManagementMessageKey, string> = {
  deleteVideoConfirm:
    '"{title}" löschen? Dies kann nicht rückgängig gemacht werden.',
  videoDeleted: 'Video gelöscht.',
  videoDeleteFailed:
    'Das Video konnte nicht gelöscht werden. Bitte versuche es erneut.',
  sessionExpired: 'Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.',
  logoutSuccess: 'Abmeldung erfolgreich! Weiterleitung...',
};

const pt: Record<VideoManagementMessageKey, string> = {
  deleteVideoConfirm: 'Excluir "{title}"? Esta ação não pode ser desfeita.',
  videoDeleted: 'Vídeo excluído.',
  videoDeleteFailed: 'Não foi possível excluir o vídeo. Tente novamente.',
  sessionExpired: 'Sua sessão expirou. Faça login novamente.',
  logoutSuccess: 'Logout realizado! Redirecionando...',
};

const ja: Record<VideoManagementMessageKey, string> = {
  deleteVideoConfirm: '「{title}」を削除しますか？この操作は元に戻せません。',
  videoDeleted: '動画を削除しました。',
  videoDeleteFailed: '動画を削除できませんでした。もう一度お試しください。',
  sessionExpired: 'セッションが期限切れです。もう一度ログインしてください。',
  logoutSuccess: 'ログアウトしました！リダイレクトしています...',
};

const MESSAGES: Record<
  AppLanguage,
  Record<VideoManagementMessageKey, string>
> = {
  en,
  zh,
  es,
  fr,
  de,
  pt,
  ja,
};

export function videoManagementMessage(
  key: VideoManagementMessageKey,
  vars: VideoManagementMessageVars = {},
  language: AppLanguage = detectLanguage()
): string {
  const template = MESSAGES[language]?.[key] ?? en[key];
  return template.replace(/\{(\w+)\}/g, (_, name: string) =>
    String(vars[name] ?? '')
  );
}
